import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';

import Constants from '../Constants';

import { useNavigate } from 'react-router-dom';

import wordmark from '../assets/menu-wordmark.jpg';
import '../App.css';

const c_IntervalMonth = 'month';
const c_IntervalYear = 'year';

// A metered feature carries a period (credits per month). One with no period is an on/off feature,
// included or not according to is_enabled.
const c_UnmeteredPeriod = 'none';

function SubscriptionPlansView({ onSelectPlan }) {
	const [plans, setPlans] = useState([]);
	const [features, setFeatures] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [selectedInterval, setSelectedInterval] = useState(c_IntervalMonth);

	const navigate = useNavigate();

	// TEMP HACK FOR TESTING
	localStorage.setItem(Constants.SIGNUP_USER_EMAIL, Constants.TEST_SIGNUP_EMAIL,  { path: "/" });

	useEffect(() => {
		async function fetchPlans() {
			try {
				const plans_url = Constants.SERVER_URL + '/api/plans';
				const response = await axios.get(plans_url);
				if(response.data && Array.isArray(response.data)) {
					response.data.sort((a, b) => a.price_in_cents - b.price_in_cents);
					setPlans(response.data);

					const featureMap = {};
					for (const plan of response.data) {
						const features_url = Constants.SERVER_URL + '/api/features/' + plan.id;
						const result = await axios.get(features_url);
						featureMap[plan.id] = result.data || [];
					}
					setFeatures(featureMap);
				}
			} catch (err) {
				setError('Failed to load subscription plans.');
				console.error('Error fetching plans:', err.message);
			} finally {
				setLoading(false);
			}
		}
		fetchPlans();
	}, []);

	function handleSelectPlan(plan) {
		if(!plan) {
			return;
		}

		if(onSelectPlan) {
			onSelectPlan(plan);
		}

		localStorage.setItem(Constants.SIGNUP_SELECTED_PLAN, JSON.stringify(plan));

		// TEMPORARY HACK - CHECKOUT BYPASS. RESTORE navigate('/payment') WHEN STRIPE CHECKOUT IS
		// ENABLED FOR AEO AUDITED. Goes straight to signup, which sends the plan stored above.
		navigate('/signup');
	}

	const filteredPlans = plans.filter((plan) => plan.billing_interval === selectedInterval);

	function getDiscountText() {
		const monthlyPlans = plans.filter((plan) => plan.billing_interval === c_IntervalMonth);
		const annualPlans = plans.filter((plan) => plan.billing_interval === c_IntervalYear);

		const planDiscounts = [];

		for(const monthlyPlan of monthlyPlans) {
			const matchingAnnualPlan = annualPlans.find((plan) => plan.name === monthlyPlan.name);
			if(matchingAnnualPlan &&
				monthlyPlan.price_in_cents &&
				monthlyPlan.price_in_cents > 0 &&
				matchingAnnualPlan.price_in_cents &&
				matchingAnnualPlan.price_in_cents > 0){
				const totalMonthlyCost = monthlyPlan.price_in_cents * 12;
				const discountPercentage = 100 - (matchingAnnualPlan.price_in_cents / totalMonthlyCost) * 100;

				if(!isNaN(discountPercentage) && discountPercentage > 0 && discountPercentage < 100) {
					planDiscounts.push(discountPercentage);
				}
			}
		}

		if(planDiscounts.length === 0) {
			return '';
		}

		const averageDiscount = planDiscounts.reduce((sum, value) => sum + value, 0) / planDiscounts.length;
		return 'Save about ' + averageDiscount.toFixed(0) + '% with annual billing';
	}

	function getIntervalLabel(billingInterval) {
		if(billingInterval === c_IntervalYear) {
			return 'year';
		}
		return 'month';
	}

	function getIntervalButtonClass(interval) {
		if(selectedInterval === interval) {
			return 'plans-interval-button plans-interval-button-active';
		}
		return 'plans-interval-button';
	}

	function renderLoading() {
		return (
			<div className="plans-loading">
				<Spinner animation="border" role="status" />
				<p className="plans-loading-text">Loading plans...</p>
			</div>
		);
	}

	function renderError() {
		if(!error) {
			return null;
		}
		return <Alert variant="danger">{error}</Alert>;
	}

	function renderDiscountText() {
		const discountText = getDiscountText();
		if(!discountText) {
			return null;
		}
		return <p className="plans-discount-text">{discountText}</p>;
	}

	function renderToggleButtons() {
		return (
			<div className="plans-interval-toggle">
				{renderDiscountText()}
				<Button
					className={getIntervalButtonClass(c_IntervalMonth)}
					onClick={() => setSelectedInterval(c_IntervalMonth)}
				>
					Monthly
				</Button>
				<Button
					className={getIntervalButtonClass(c_IntervalYear)}
					onClick={() => setSelectedInterval(c_IntervalYear)}
				>
					Annual
				</Button>
			</div>
		);
	}

	// Credits read as the feature's own description ("1,000 single page analyses per month"),
	// rather than its internal name and a raw decimal limit.
	function renderMeteredFeature(feature, uniqueKey) {
		var label = feature.description;
		if(!label) {
			label = feature.name + ': ' + Math.floor(feature.limit_value).toLocaleString('en-US');
		}

		return (
			<li key={uniqueKey} className="plan-feature plan-feature-metered">
				<span className="plan-feature-icon plan-feature-icon-included">{'✓'}</span>
				<span className="plan-feature-label">{label}</span>
			</li>
		);
	}

	function renderToggleFeature(feature, uniqueKey) {
		if(feature.is_enabled) {
			return (
				<li key={uniqueKey} className="plan-feature">
					<span className="plan-feature-icon plan-feature-icon-included">{'✓'}</span>
					<span className="plan-feature-label">{feature.name}</span>
				</li>
			);
		}

		return (
			<li key={uniqueKey} className="plan-feature plan-feature-excluded">
				<span className="plan-feature-icon plan-feature-icon-excluded">{'✕'}</span>
				<span className="plan-feature-label">{feature.name}</span>
			</li>
		);
	}

	function renderFeature(feature, planId) {
		const uniqueKey = planId + '_' + feature.code + '_' + feature.id;

		if(feature.limit_period && (feature.limit_period !== c_UnmeteredPeriod)) {
			return renderMeteredFeature(feature, uniqueKey);
		}

		return renderToggleFeature(feature, uniqueKey);
	}

	function renderFeatures(planId) {
		if(!features[planId] || features[planId].length === 0) {
			return null;
		}

		const planFeatures = features[planId].slice();
		planFeatures.sort((a, b) => a.sort_order - b.sort_order);

		return (
			<ul className="plan-features">
				{planFeatures.map((feature) => renderFeature(feature, planId))}
			</ul>
		);
	}

	function renderPlanPrice(plan) {
		if(!plan.price_in_cents || !plan.billing_interval) {
			return (
				<div className="plan-price plan-price-unavailable">
					Price not available
				</div>
			);
		}

		return (
			<div className="plan-price">
				<span className="plan-price-amount">{'$' + (plan.price_in_cents / 100).toFixed(2)}</span>
				<span className="plan-price-interval">{' / ' + getIntervalLabel(plan.billing_interval)}</span>
			</div>
		);
	}

	function renderPlanCard(plan) {
		return (
			<Col lg={4} md={6} key={plan.id} className="plan-card-col">
				<Card className="plan-card">
					<Card.Body className="plan-card-body">
						<Card.Title className="plan-name">{plan.name}</Card.Title>
						{renderPlanPrice(plan)}
						<Card.Text className="plan-description">{plan.description}</Card.Text>

						{renderFeatures(plan.id)}

						<Button
							variant="primary"
							className="plan-select-button"
							onClick={() => handleSelectPlan(plan)}
						>
							Select Plan
						</Button>
					</Card.Body>
				</Card>
			</Col>
		);
	}

	function renderPlansList() {
		if(!filteredPlans || filteredPlans.length === 0) {
			return (
				<p className="plans-empty">No plans available.</p>
			);
		}

		return (
			<Row className="plans-row">
				{filteredPlans.map((plan) => renderPlanCard(plan))}
			</Row>
		);
	}

	function renderMainContent() {
		if(loading) {
			return renderLoading();
		}

		return (
			<React.Fragment>
				{renderError()}
				{renderToggleButtons()}
				{renderPlansList()}
			</React.Fragment>
		);
	}

	return (
		<Container fluid className="plans-container">
			<Row className="plans-outer-row">
				<Col xl={9} lg={10} md={11} sm={12}>
					<div className="plans-header">
						<img src={wordmark} className="plans-logo" alt="AEO Audited"/>
						<h3 className="plans-title">Choose Your Plan</h3>
						<p className="plans-subtitle">
							One credit is one single page analysis.
						</p>
					</div>
					{renderMainContent()}
				</Col>
			</Row>
		</Container>
	);
}

export default SubscriptionPlansView;
